import React, { useEffect, useRef, useState } from "react";
import FeedbacksSummary from "../../components/ManageFeedbacks/FeedbacksSummary/FeedbacksSummary";
import FeedbacksFilter from "../../components/ManageFeedbacks/FeedbacksFilter/FeedbacksFilter";
import FeedbacksContainer from "../../components/ManageFeedbacks/FeedbacksContainer/FeedbacksContainer";
import "./manageFeedbacks.css";
import { Assignee, FeedbackSummaryData } from "./manageFeedbacksTypes";
import dayjs, { Dayjs } from "dayjs";
import { Loader } from "../../components/loader/Loader";
import {
  fetchAllFeedbacksData,
  fetchAssigneeDataFromConfig,
  fetchBrandsData,
  fetchFeedbackSummaryData,
  fetchFeedbackTypeDataFromConfig,
  fetchFlavoursData,
  fetchManufacturingLocationsDataFromConfig,
} from "./manageFeedbacksService";
import { popupType } from "../../types/types";
import { GenericPopUp } from "../../components/popup/genericPopUp";
const ManageFeedbacks = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [dateFilter, setDateFilter] = useState<Record<string, Dayjs>>({
    fromDate: dayjs().subtract(1, "month"), // One month back from today
    toDate: dayjs(), // Today's date
  });
  const [typeAndStatusFilter, setTypeAndStatusFilter] = useState<
    Record<string, string>
  >({ feedbackType: "All", feedbackStatus: "All" });
  const [genericModalState, setGenericModalState] = useState(false);
  const [genericModalMessage, setGenericModalSMessage] = useState("");
  const [genericModaltype, setGenericModalType] = useState<popupType>("Error");
  const feedbackSummaryData = useRef<FeedbackSummaryData[]>([]);
  const allFeedbacksData = useRef<Record<string, any>[]>([]);
  const brandsData = useRef<string[]>([]);
  const flavoursData = useRef<string[]>([]);
  const manufacturingLocationsData = useRef<string[]>([]);
  const supportAssigneeData = useRef<Assignee[]>([]);
  const feedbackTypeList = useRef<string[]>([]);
  const updateDate = (newDate: Dayjs, type: "to" | "from") => {
    setDateFilter((prev) => {
      const currentDate = type === "from" ? prev.fromDate : prev.toDate;

      // Only call setState if the value has changed
      if (currentDate === newDate) return prev; // Prevent unnecessary re-render

      return {
        ...prev,
        [type === "from" ? "fromDate" : "toDate"]: newDate,
      };
    });
  };

  const updateFeedbackType = (feedbackType: string) => {
    setTypeAndStatusFilter((prev) => {
      if (prev.feedbackType === feedbackType) return prev; // Prevent unnecessary re-render

      return {
        ...prev,
        feedbackType: feedbackType,
      };
    });
  };

  const updateFeedbackStatus = (feedbackStatus: string) => {
    setTypeAndStatusFilter((prev) => {
      if (prev.feedbackStatus === feedbackStatus) return prev; // Prevent unnecessary re-render
      return {
        ...prev,
        feedbackStatus: feedbackStatus,
      };
    });
  };
  const openPopUp = (message: string, modalType: popupType) => {
    setGenericModalSMessage(message);
    setGenericModalState(true);
    setGenericModalType(modalType);
  };
  const fetchUpdatedData = async () => {
    setLoading(true); // Start loading
    const errors:string[] = []; // Array to collect error messages

    try {
        // Execute all API calls concurrently using Promise.allSettled
        const results = await Promise.allSettled([
            fetchFeedbackSummaryData(), // First API call
            fetchAllFeedbacksData(dateFilter), // Second API call
            // Add other API calls here if needed
        ]);

        // Process results
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                // Assign results to respective data references
                switch (index) {
                    case 0:
                        feedbackSummaryData.current = result.value as FeedbackSummaryData[];
                        break;
                    case 1:
                        allFeedbacksData.current = result.value;
                        break;
                    // Handle more cases if additional API calls are added
                    default:
                        break;
                }
            } else {
                // Handle the error for each failed API call
                console.error(`API call ${index + 1} failed with reason:`, result.reason);
                // Add the respective error message to the errors array
                switch (index) {
                    case 0:
                        errors.push("Feedback Summary Data");
                        break;
                    case 1:
                        errors.push("All Feedbacks Data");
                        break;
                    // Add more cases for additional API calls if needed
                    default:
                        break;
                }
            }
        });

        // Show popup if there were any errors
        if (errors.length > 0) {
            openPopUp(`Errors in fetching data for: ${errors.join(", ")}`, "Error");
        }

    } catch (error) {
        openPopUp("Something Went Wrong", "Error");
        console.error("An unexpected error occurred:", error);
    } finally {
        setLoading(false); // Stop loading regardless of the outcome
    }
};
  const init = async () => {
    setLoading(true); // Start loading
    const errors: string[] = []; // Array to collect error messages

    try {
      // Execute all API calls concurrently using Promise.allSettled
      const results = await Promise.allSettled([
        fetchFeedbackSummaryData(), // 1
        fetchAllFeedbacksData(dateFilter), // 2
        fetchBrandsData(), // 3
        fetchFlavoursData(), // 4
        fetchManufacturingLocationsDataFromConfig(), // 5
        fetchAssigneeDataFromConfig(), // 6
        fetchFeedbackTypeDataFromConfig(), // 7
      ]);

      // Process results
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          // Assign the results to the respective data references
          switch (index) {
            case 0:
              feedbackSummaryData.current = result.value as FeedbackSummaryData[];
              break;
            case 1:
              allFeedbacksData.current = result.value as Record<string, any>[];
              break;
            case 2:
              brandsData.current = result.value as string[];
              break;
            case 3:
              flavoursData.current = result.value as string[];
              break;
            case 4:
              manufacturingLocationsData.current = result.value as string[];
              break;
            case 5:
              supportAssigneeData.current = result.value as Assignee[];
              break;
            case 6:
              feedbackTypeList.current = result.value as string[];
              break;
            default:
              break;
          }
        } else {
          // Handle the error for each failed API call
          console.error(
            `API call ${index + 1} failed with reason:`,
            result.reason
          );

          // Add the respective error message to the errors array
          switch (index) {
            case 0:
              errors.push("Feedback Summary Data");
              break;
            case 1:
              errors.push("All Feedbacks Data");
              break;
            case 2:
              errors.push("Brands Data");
              break;
            case 3:
              errors.push("Flavours Data");
              break;
            case 4:
              errors.push("Manufacturing Locations Data");
              break;
            case 5:
              errors.push("Support Assignee Data");
              break;
            case 6:
              errors.push("Feedback Type Data");
              break;
            default:
              break;
          }
        }
      });

      // Show popup if there were any errors
      if (errors.length > 0) {
        openPopUp(`Errors in fetching data for: ${errors.join(", ")}`, "Error");
      }
    } catch (error) {
      openPopUp("Error Fetching Data", "Error");
      console.error("An unexpected error occurred:", error);
    } finally {
      setLoading(false); // Stop loading regardless of the outcome
    }
  };

  useEffect(() => {
    init();
  }, []);
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="manage-feedbacks-wrapper">
          <FeedbacksSummary data={feedbackSummaryData.current} />
          <FeedbacksFilter
            dateFilter={dateFilter}
            typeAndStatusFilter={typeAndStatusFilter}
            updateDate={updateDate}
            updateFeedbackType={updateFeedbackType}
            updateFeedbackStatus={updateFeedbackStatus}
            feedbackTypeList={feedbackTypeList.current}
          />
          <FeedbacksContainer
            dateFilter={dateFilter}
            typeAndStatusFilter={typeAndStatusFilter}
            allFeedbacksData={allFeedbacksData.current}
            brandsData={brandsData.current}
            flavoursData={flavoursData.current}
            fetchUpdatedData={fetchUpdatedData}
            manufacturingLocations={manufacturingLocationsData.current}
            supportAssignees={supportAssigneeData.current}
          />
        </div>
      )}
      <GenericPopUp
        type={genericModaltype}
        message={genericModalMessage}
        openGenericModal={genericModalState}
        setOpenGenericModal={() => {
          setGenericModalState(false);
        }}
      />
    </>
  );
};

export default ManageFeedbacks;
